import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Users, Ticket, FileText, Layers, Loader2, Trash2 } from "lucide-react";

const AdminOverview = () => {
    const confirm = useConfirm();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [resetting, setResetting] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/stats");
            setStats(data);
        } catch {
            toast.error("Failed to load stats");
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        load();
    }, []);

    const resetData = async () => {
        const ok = await confirm({
            title: "Reset All Client Data",
            message:
                "This will permanently delete every client account, every activation code, every project, and every script. Your admin account and master strategist prompt will be kept. This cannot be undone.",
            confirmLabel: "Yes, Wipe Everything",
            cancelLabel: "Keep Data",
            variant: "danger",
        });
        if (!ok) return;
        setResetting(true);
        try {
            const { data } = await api.post("/admin/reset-data");
            toast.success(
                `Cleared: ${data.clients_deleted} clients, ${data.codes_deleted} codes, ${data.projects_deleted} projects, ${data.scripts_deleted} scripts`,
            );
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Reset failed");
        } finally {
            setResetting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="admin-overview-loading">
                <Loader2 className="w-6 h-6 animate-spin text-un-berry" />
            </div>
        );
    }

    const cards = [
        { label: "Total Clients", value: stats.total_clients, Icon: Users },
        { label: "Active Clients", value: stats.active_clients, Icon: Users },
        { label: "Unused Codes", value: stats.unused_codes, Icon: Ticket },
        { label: "Projects", value: stats.total_projects, Icon: Layers },
        { label: "Scripts Generated", value: stats.total_scripts, Icon: FileText },
    ];

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="admin-overview">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
                <div>
                    <div className="un-label text-un-grape mb-3">Admin</div>
                    <h1 className="un-h1 text-5xl md:text-6xl mb-2">
                        Command <span className="text-un-berry">Center</span>.
                    </h1>
                    <p className="text-un-onyx/70 max-w-2xl">
                        Manage clients, issue activation codes, and tune the master strategist prompt.
                    </p>
                </div>
                <button
                    onClick={resetData}
                    disabled={resetting}
                    className="un-btn-outline text-sm flex items-center gap-2 border-un-berry text-un-berry hover:!bg-un-berry hover:!text-white"
                    data-testid="reset-data-button"
                >
                    {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    {resetting ? "Wiping…" : "Reset All Data"}
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-0 border-2 border-un-onyx mb-12">
                {cards.map((c, i) => {
                    const I = c.Icon;
                    return (
                        <div
                            key={c.label}
                            className={`p-6 ${i < cards.length - 1 ? "md:border-r-2 border-un-onyx" : ""}`}
                            data-testid={`stat-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            <I className="w-5 h-5 text-un-berry mb-4" />
                            <div className="un-label text-un-grape mb-2">{c.label}</div>
                            <div className="un-h1 text-5xl">{c.value}</div>
                        </div>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Link to="/admin/clients" className="un-card un-card-hover p-6" data-testid="quick-clients">
                    <Users className="w-6 h-6 text-un-berry mb-4" />
                    <div className="un-h3 mb-1">Clients</div>
                    <div className="text-un-onyx/60 text-sm">Review who has access. Edit or deactivate.</div>
                </Link>
                <Link to="/admin/codes" className="un-card un-card-hover p-6" data-testid="quick-codes">
                    <Ticket className="w-6 h-6 text-un-berry mb-4" />
                    <div className="un-h3 mb-1">Activation Codes</div>
                    <div className="text-un-onyx/60 text-sm">Issue new invites, revoke unused codes.</div>
                </Link>
                <Link to="/admin/prompt" className="un-card un-card-hover p-6" data-testid="quick-prompt">
                    <FileText className="w-6 h-6 text-un-berry mb-4" />
                    <div className="un-h3 mb-1">Prompt Config</div>
                    <div className="text-un-onyx/60 text-sm">Edit the master strategist system prompt.</div>
                </Link>
            </div>
        </div>
    );
};

export default AdminOverview;
